import { useState, useRef, useEffect, type ReactNode } from "react";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { File, FileText, Headphones, Images, Plus, Smile, MessageSquareText, Workflow } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import type { EmojiClickData } from "emoji-picker-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { AudioRecorder } from "./AudioRecorder";
import { TemplatesMenu } from "./TemplatesMenu";
import { WhatsAppTemplatePicker } from "./WhatsAppTemplatePicker";
import { FluxosPicker } from "./FluxosPicker";
import { useTemplates } from "@/hooks/useTemplates";
import { renderMessageVariables } from "@/lib/messageVariables";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/supabase";
import type { Fluxo } from "@/hooks/useFluxos";
import type { WaTemplate } from "@/data/whatsappTemplateMocks";
import { useTranslation } from "react-i18next";

type AccountTemplate = Database['public']['Tables']['account_templates']['Row']
type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface MessageInputProps {
  /**
   * Receives the trimmed draft and resolves to whether it left. The draft is cleared
   * optimistically and restored here when this resolves false, so the composer owns the
   * text and a keystroke never re-renders the conversation list or the message list.
   */
  onSendMessage: (text: string) => Promise<boolean> | boolean | void;
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  onSendImage: (imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendVideo: (videoBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendDocument: (documentBase64: string, fileName: string, caption?: string) => Promise<boolean>;
  onSendFluxo?: (fluxo: Fluxo) => void;
  sending?: boolean;
  selectedContact?: AccountContact | null;
  /** Barra de citação acima do campo; ausente quando não se está respondendo. */
  replyBar?: ReactNode;
}

export const MessageInput = ({
  onSendMessage,
  onSendAudio,
  onSendImage,
  onSendVideo,
  onSendDocument,
  onSendFluxo,
  sending = false,
  selectedContact = null,
  replyBar = null,
}: MessageInputProps) => {
  const { t } = useTranslation("chat");
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTemplatesMenu, setShowTemplatesMenu] = useState(false);
  const [showFluxosPicker, setShowFluxosPicker] = useState(false);
  const [showWaTemplatePicker, setShowWaTemplatePicker] = useState(false);
  const [showShortcutSuggestions, setShowShortcutSuggestions] = useState(false);
  const [matchedTemplates, setMatchedTemplates] = useState<AccountTemplate[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const templatesMenuRef = useRef<HTMLDivElement>(null);
  const fluxosPickerRef = useRef<HTMLDivElement>(null);
  const waTemplatePickerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { templates, loading: templatesLoading, getTemplateByShortcut } = useTemplates();

  // Trocar de conversa limpa o rascunho — mesma regra que já vale para a citação
  // pendente em ChatInterface, agora também quando a troca vem pela URL.
  useEffect(() => {
    setMessage("");
    setShowShortcutSuggestions(false);
  }, [selectedContact?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (templatesMenuRef.current && !templatesMenuRef.current.contains(event.target as Node)) {
        setShowTemplatesMenu(false);
      }
      if (fluxosPickerRef.current && !fluxosPickerRef.current.contains(event.target as Node)) {
        setShowFluxosPicker(false);
      }
      if (waTemplatePickerRef.current && !waTemplatePickerRef.current.contains(event.target as Node)) {
        setShowWaTemplatePicker(false);
      }
    };

    if (showEmojiPicker || showTemplatesMenu || showFluxosPicker || showWaTemplatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker, showTemplatesMenu, showFluxosPicker, showWaTemplatePicker]);

  useEffect(() => {
    const lastWord = message.split(/\s/).pop() || '';

    if (lastWord.startsWith('/') && lastWord.length > 1) {
      const shortcut = lastWord;
      const matched = templates.filter(t =>
        t.shortcut && t.shortcut.toLowerCase().startsWith(shortcut.toLowerCase())
      );

      if (matched.length > 0) {
        console.log('📝 [MessageInput] Matched shortcuts:', matched.map(t => t.shortcut));
        setMatchedTemplates(matched);
        setShowShortcutSuggestions(true);
      } else {
        setShowShortcutSuggestions(false);
      }
    } else {
      setShowShortcutSuggestions(false);
    }
  }, [message, templates]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessage(message + emojiData.emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowTemplatesMenu(false);
    setShowFluxosPicker(false);
    setShowWaTemplatePicker(false);
  };

  const toggleTemplatesMenu = () => {
    setShowTemplatesMenu(!showTemplatesMenu);
    setShowFluxosPicker(false);
    setShowWaTemplatePicker(false);
    setShowEmojiPicker(false);
  };

  const toggleFluxosPicker = () => {
    setShowFluxosPicker(!showFluxosPicker);
    setShowTemplatesMenu(false);
    setShowWaTemplatePicker(false);
    setShowEmojiPicker(false);
  };

  const handleSelectTemplate = (template: AccountTemplate) => {
    console.log('✅ [MessageInput] Template selected:', template.name);
    const rendered = renderMessageVariables(template.content, selectedContact);
    setMessage(rendered);
    setShowTemplatesMenu(false);
    setShowShortcutSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleSelectShortcutSuggestion = (template: AccountTemplate) => {
    console.log('✅ [MessageInput] Shortcut suggestion selected:', template.shortcut);
    const rendered = renderMessageVariables(template.content, selectedContact);
    const words = message.split(/\s/);
    words[words.length - 1] = rendered;
    setMessage(words.join(' '));
    setShowShortcutSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleSendWaTemplate = (template: WaTemplate) => {
    setMessage(template.body);
    setShowWaTemplatePicker(false);
    textareaRef.current?.focus();
  };

  const handleSendClick = async () => {
    const rawText = message.trim();
    if (!rawText || sending) return;

    // Limpa na hora e devolve o texto se o envio falhar — mesmo comportamento de antes,
    // só que agora o rascunho mora aqui.
    setMessage("");
    setShowShortcutSuggestions(false);

    const sent = await onSendMessage(rawText);
    if (sent === false) {
      setMessage(rawText);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending) {
        handleSendClick();
      }
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const openPicker = (ref: { current: HTMLInputElement | null }) => {
    setAttachOpen(false);
    ref.current?.click();
  };

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isMp4Video =
      file.type === 'video/mp4' ||
      file.name.toLowerCase().endsWith('.mp4');

    if (!isImage && !isMp4Video) {
      alert(t("input.errPickImageVideo"));
      return;
    }

    const isVideo = isMp4Video && !isImage;
    const maxSize = isVideo ? 64 * 1024 * 1024 : 16 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isVideo ? 'O vídeo deve ter no máximo 64MB' : 'A imagem deve ter no máximo 16MB');
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      if (isVideo) {
        await onSendVideo(base64, '', file.name);
      } else {
        await onSendImage(base64, '', file.name);
      }
    } catch (error) {
      console.error('Erro ao enviar mídia:', error);
      alert(t("input.errSendMedia"));
    } finally {
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    }
  };

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert(t("input.errDocumentSize"));
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      await onSendDocument(base64, file.name, '');
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      alert(t("input.errSendDocument"));
    } finally {
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  const handleAudioFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      alert(t("input.errPickAudio"));
      return;
    }

    const maxSize = 16 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(t("input.errAudioSize"));
      return;
    }

    try {
      if (!supabase) {
        alert(t("input.errNotConfigured"));
        return;
      }

      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || `audio_${timestamp}`;
      const filePath = `audios/${timestamp}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-audios')
        .upload(filePath, file, {
          contentType: file.type || 'audio/mpeg',
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('❌ Storage upload failed:', uploadError);
        alert(t("input.errUploadAudio"));
        return;
      }

      const { data: urlData } = supabase.storage.from('chat-audios').getPublicUrl(filePath);
      await onSendAudio(urlData.publicUrl);
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
      alert(t("input.errSendAudio"));
    } finally {
      if (audioInputRef.current) audioInputRef.current.value = '';
    }
  };

  const attachItemClass =
    "w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] bg-transparent hover:bg-hover dark:hover:bg-[#27272A] text-left disabled:opacity-50 text-foreground transition-colors";

  return (
    <div className="p-3 relative">
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,video/mp4,.mp4"
        onChange={handleMediaChange}
        className="hidden"
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
        onChange={handleDocumentChange}
        className="hidden"
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={handleAudioFileChange}
        className="hidden"
      />
      <div className="flex flex-col gap-2">
        {replyBar}
        <div className="flex-1">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("input.placeholder")}
            className="resize-none border border-border rounded-[14px] min-h-[60px] text-[13px] font-normal bg-card text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:placeholder:text-[#71717A] dark:text-neutral-50 dark:rounded-[6px] dark:focus-visible:border-ring dark:focus-visible:ring-ring/50 dark:focus-visible:ring-[1px] dark:shadow-xs"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2 text-muted-foreground dark:text-neutral-50">
            <Smile
              className="w-4 h-4 cursor-pointer hover:text-primary dark:hover:text-neutral-300 transition-colors duration-200"
              onClick={toggleEmojiPicker}
            />
            <Popover open={attachOpen} onOpenChange={setAttachOpen}>
              <PopoverTrigger asChild>
                <button type="button" aria-label={t("input.attach")} className="inline-flex">
                  <Plus className="w-4 h-4 cursor-pointer hover:text-primary dark:hover:text-neutral-300 transition-colors duration-200" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="!bg-card p-2 w-[220px] flex flex-col gap-0.5 border-border border shadow-lg rounded-[14px] dark:!bg-[#18181B] dark:border-[#3F3F464D]"
              >
                <button
                  type="button"
                  onClick={() => openPicker(documentInputRef)}
                  disabled={sending}
                  className={attachItemClass}
                >
                  <File className="w-5 h-5 shrink-0 text-[#7C5CFC]" />
                  <span className="text-[14px] font-medium">{t("input.document")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => openPicker(mediaInputRef)}
                  disabled={sending}
                  className={attachItemClass}
                >
                  <Images className="w-5 h-5 shrink-0 text-[#2B9AF3]" />
                  <span className="text-[14px] font-medium">{t("input.photosVideos")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => openPicker(audioInputRef)}
                  disabled={sending}
                  className={attachItemClass}
                >
                  <Headphones className="w-5 h-5 shrink-0 text-[#F6851B]" />
                  <span className="text-[14px] font-medium">{t("input.audio")}</span>
                </button>
              </PopoverContent>
            </Popover>

            <div className="relative">
              <FileText
                className="w-4 h-4 cursor-pointer hover:text-primary dark:hover:text-neutral-300 transition-colors duration-200"
                onClick={toggleTemplatesMenu}
              />
              {showTemplatesMenu && (
                <div ref={templatesMenuRef} className="absolute bottom-full left-0 mb-2 z-50">
                  <TemplatesMenu
                    templates={templates}
                    onSelectTemplate={handleSelectTemplate}
                    loading={templatesLoading}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <Workflow
                className="w-4 h-4 cursor-pointer hover:text-primary dark:hover:text-neutral-300 transition-colors duration-200"
                onClick={toggleFluxosPicker}
              />
              {showFluxosPicker && (
                <div ref={fluxosPickerRef} className="absolute bottom-full left-0 mb-2 z-50">
                  <FluxosPicker
                    onClose={() => setShowFluxosPicker(false)}
                    onSelect={onSendFluxo}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <MessageSquareText
                className="w-4 h-4 cursor-pointer hover:text-primary dark:hover:text-neutral-300 transition-colors duration-200"
                onClick={() => {
                  setShowWaTemplatePicker(!showWaTemplatePicker);
                  setShowTemplatesMenu(false);
                  setShowFluxosPicker(false);
                  setShowEmojiPicker(false);
                }}
              />
              {showWaTemplatePicker && (
                <div ref={waTemplatePickerRef} className="absolute bottom-full left-0 mb-2 z-50">
                  <WhatsAppTemplatePicker
                    onClose={() => setShowWaTemplatePicker(false)}
                    onSend={handleSendWaTemplate}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSendClick}
              disabled={!message.trim() || sending}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white min-w-[90px] rounded-[12px] shadow-sm transition-colors duration-200"
            >
              {sending ? 'Enviando...' : 'Enviar'}
            </Button>
            <AudioRecorder onSendAudio={onSendAudio} disabled={sending} />
          </div>
        </div>
      </div>

      {showEmojiPicker && (
        <div ref={emojiPickerRef} className="absolute bottom-1/2 left-0 mb-2 z-50">
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={document.documentElement.classList.contains('dark') ? Theme.DARK : Theme.LIGHT}
          />
        </div>
      )}

      {showShortcutSuggestions && matchedTemplates.length > 0 && (
        <div className="absolute bottom-full left-0 mb-2 w-full max-w-[360px] bg-card rounded-lg shadow-md border border-border overflow-hidden z-50 dark:bg-neutral-800 dark:border-neutral-700 dark:shadow-xl">
          <div className="p-2 border-b border-border dark:border-neutral-700">
            <p className="text-xs text-muted-foreground dark:text-neutral-400">{t("input.tabToUse")}</p>
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {matchedTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleSelectShortcutSuggestion(template)}
                className="w-full text-left px-3 py-2 hover:bg-hover dark:hover:bg-neutral-700 transition-colors duration-200"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-[#EEF4FF] text-primary border border-border dark:bg-neutral-900 dark:text-blue-400 dark:border-neutral-600">
                    {template.shortcut}
                  </span>
                  <span className="text-sm font-medium text-foreground dark:text-neutral-50">
                    {template.name}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground dark:text-neutral-400 line-clamp-2">
                  {template.content}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
